/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { presentUnexpectedFailure } from '../../../shared/sentry/presentUnexpectedFailure';
import { RefreshControl, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { useNavigation, useRoute } from '@react-navigation/native';
import type { KeyboardAwareScrollViewRef } from 'react-native-keyboard-controller';
import { spacing } from '../../../shared/theme/spacing'
import { typography } from '../../../shared/theme/typography';
import { ticketColors } from '../../../shared/theme/ticketColors';
import { ShellContentFrame } from '../../../shared/layout/ShellContentFrame'
import { ShellScrollScreen } from '../../../shared/layout/ShellScrollScreen'
import { ShellScreenLoading } from '../../../shared/layout/ShellScreenLoading'
import { useContentLayout } from '../../../shared/layout/useContentLayout';
import { settingsStyles } from '../../../settings/screens/settingsScreen.styles';
import { FiscalStatusCard } from '../../components/FiscalStatusCard';
import { SettingsNavigationRow } from '../../../settings/components/SettingsNavigationRow';
import { LibroIvaExportSection } from '../../components/LibroIvaExportSection';
import { formatCuitDisplay, spanishTaxRegimeLabel } from '../../domain/fiscalPresentation';
import { formatLocalDateTime } from '@shared/vueltoDateTime';
import { formatMoney } from '../../../shared/format/public-api';
import { BranchFiscalSettingsPanel, branchFiscalRowsFromSummaries } from '../../components/BranchFiscalSettingsPanel'
import type { BranchFiscalRowState } from '../../components/BranchFiscalSettingsPanel';
import { useSessionStore } from '../../../shell/session/store/useSessionStore';
import { useBranchPermission } from '../../../shell/session/hooks/useBranchPermission';
import { usePlanEntitlements } from '../../../shell/providers/PlanEntitlementsProvider';
import { fetchFiscalYtdTotals, fetchCreditNotesForBranch, saveFiscalProfileRemote, validateBranchPointOfSale } from '../../infra/fiscalRepository'
import type { CreditNoteListRow } from '../../infra/fiscalRepository'
import { getFiscalContext } from '../../infra/fiscalConfigRepo'
import type { FiscalContext } from '../../domain/fiscalTypes';
import { navigateInShell } from '../../../shell/navigation/rootNavigation';
import { useScreenLoadShell } from '../../../shared/ui/hooks-screen/useScreenLoad';
import { clearArcaWizardDraft } from '../../infra/arcaWizardDraftStorage';

type HubTab = 'status' | 'setup' | 'branches';

const TAB_OPTIONS = [
  { value: 'status' as const, label: 'Estado' },
  { value: 'setup' as const, label: 'Configuración' },
  { value: 'branches' as const, label: 'Sucursales' },
];

/** Local brandFill segmented — hub tabs only (does not touch operationalFilters.styles). */
function HubTabSegmented({ value, onChange }: { value: HubTab; onChange: (tab: HubTab) => void }) {
  return (
    <View style={hubTabStyles.track} accessibilityRole="tablist">
      {TAB_OPTIONS.map((option) => {
        const active = value === option.value;
        return (
          <TouchableOpacity
            key={option.value}
            style={[hubTabStyles.segment, active && hubTabStyles.segmentActive]}
            onPress={() => onChange(option.value)}
            accessibilityRole="tab"
            accessibilityState={{ selected: active }}
            accessibilityLabel={option.label}
            testID={`fiscal.hub.tab-${option.value}`}
          >
            <Text
              style={[hubTabStyles.segmentText, active && hubTabStyles.segmentTextActive]}
              numberOfLines={1}
            >
              {option.label}
            </Text>
          </TouchableOpacity>
        );
      })}
    </View>
  );
}

export function FiscalHubScreen() {
  const navigation = useNavigation<any>();
  const route = useRoute<any>();
  const initialTab = (route.params?.initialTab as HubTab | undefined) ?? 'status';
  const autoOpenWizard = route.params?.autoOpenWizard === true;
  const currentBranchId = useSessionStore((s) => s.currentBranchId);
  const branches = useSessionStore((s) => s.branches);
  const { canView, canEdit, canViewOthers } = useBranchPermission('BranchSettings');
  const { entitlements } = usePlanEntitlements();
  const isOwner = entitlements?.is_owner === true;
  const canEditFiscal = isOwner && canEdit;
  const canViewAllBranches = isOwner || canViewOthers;
  const { layoutWebShell } = useContentLayout('settings');
  const scrollRef = useRef<KeyboardAwareScrollViewRef>(null);

  const [tab, setTab] = useState<HubTab>(initialTab);
  const [loading, setLoading] = useState(true);
  const [ctx, setCtx] = useState<FiscalContext | null>(null);
  const [ytdOrg, setYtdOrg] = useState<number | null>(null);
  const [ytdBranch, setYtdBranch] = useState<number | null>(null);
  const [branchPvMap, setBranchPvMap] = useState<Record<string, string>>({});
  const [rowValidating, setRowValidating] = useState<Record<string, boolean>>({});
  const [validatingAll, setValidatingAll] = useState(false);
  const [creditNotes, setCreditNotes] = useState<CreditNoteListRow[]>([]);

  const load = useCallback(async (opts?: { force?: boolean }) => {
    if (!currentBranchId || !canView) {
      setLoading(false);
      return;
    }
    if (opts?.force) setLoading(true);
    try {
      // BUG-1 fix: propagar force para que el pull-to-refresh y post-write bypassen el cache.
      const nextCtx = await getFiscalContext(currentBranchId, {
        includeAllBranches: canViewAllBranches,
        force: opts?.force === true,
      });
      setCtx(nextCtx);
      if (nextCtx.profile?.setupStatus === 'validated' && nextCtx.profile.organizationId) {
        void clearArcaWizardDraft(nextCtx.profile.organizationId);
      }
      const branchIds = branches.map((branch) => branch.id);
      const { orgTotal, branchTotal } = await fetchFiscalYtdTotals(branchIds, currentBranchId).catch(() => ({
        orgTotal: 0,
        branchTotal: 0,
      }));
      setYtdOrg(orgTotal);
      setYtdBranch(branchTotal);
      const notes = await fetchCreditNotesForBranch(currentBranchId).catch(() => []);
      setCreditNotes(notes);
      const map: Record<string, string> = {};
      for (const row of nextCtx.branchesFiscal ?? []) {
        if (row.pointOfSale != null) map[row.branchId] = String(row.pointOfSale);
      }
      setBranchPvMap(map);
    } finally {
      setLoading(false);
    }
  }, [branches, canView, canViewAllBranches, currentBranchId]);

  useEffect(() => {
    if (route.params?.initialTab) setTab(route.params.initialTab as HubTab);
  }, [route.params?.initialTab]);

  useEffect(() => {
    if (!autoOpenWizard || loading || !currentBranchId) return;
    if (ctx?.profile?.setupStatus !== 'validated') {
      navigation.replace('ArcaIntegration');
    }
  }, [autoOpenWizard, loading, currentBranchId, ctx?.profile?.setupStatus, navigation]);

  const { refreshing, onRefresh } = useScreenLoadShell(
    'FiscalHub',
    async (opts) => {
      if (!currentBranchId) return { silent: true };
      await load(opts);
    }
  );

  const rows: BranchFiscalRowState[] = useMemo(
    () =>
      branchFiscalRowsFromSummaries(ctx?.branchesFiscal, branchPvMap, branches).map((row) => ({
        ...row,
        validating: rowValidating[row.branchId] === true,
      })),
    [branchPvMap, branches, ctx?.branchesFiscal, rowValidating]
  );

  const handleValidateBranch = async (branchId: string) => {
    if (!canEditFiscal) return;
    const pv = Number(branchPvMap[branchId]);
    if (!Number.isFinite(pv) || pv <= 0 || !ctx?.profile) return;
    setRowValidating((prev) => ({ ...prev, [branchId]: true }));
    try {
      await saveFiscalProfileRemote(branchId, {
        taxRegime: ctx.profile.taxRegime,
        emitterCuit: ctx.profile.emitterCuit ?? '',
        businessName: ctx.profile.businessName ?? '',
        // Reenvía los datos del emisor ya cargados: el upsert pisa a null lo que no viene.
        domicilioComercial: ctx.profile.domicilioComercial,
        iibb: ctx.profile.iibb,
        inicioActividades: ctx.profile.inicioActividades,
        monotributoCategory: ctx.profile.monotributoCategory,
        environment: ctx.profile.environment,
        pointOfSale: pv,
      });
      const result = await validateBranchPointOfSale(branchId, pv);
      if (!result.ok) throw new Error(result.error ?? 'Validación fallida');
      // BUG-1 fix: forzar refresh del contexto (el cache fue invalidado por saveFiscalProfileRemote
      // y validateBranchPointOfSale, pero load() debe hacer fetch fresco).
      await load({ force: true });
    } catch (err: unknown) {
      // BUG-2 fix: capturar error y mostrar feedback al usuario.
      presentUnexpectedFailure({ module: 'fiscal', op: 'validaci-n', error: err, title: 'Validación', userMessage: 'No se pudo validar el PV.' });
    } finally {
      setRowValidating((prev) => ({ ...prev, [branchId]: false }));
    }
  };

  const isValidated = ctx?.profile?.setupStatus === 'validated';

  const openProductionSetup = useCallback(() => {
    navigateInShell(navigation, 'ArcaIntegration', { cutoverToProduction: true });
  }, [navigation]);

  const emitterSummary = useMemo(() => {
    const p = ctx?.profile;
    if (!p) return 'CUIT, razón social, régimen, domicilio e IIBB.';
    const parts = [spanishTaxRegimeLabel(p.taxRegime)];
    if (p.monotributoCategory) parts.push(`categoría ${p.monotributoCategory}`);
    if (p.emitterCuit) parts.push(`CUIT ${formatCuitDisplay(p.emitterCuit)}`);
    return parts.join(' · ');
  }, [ctx?.profile]);

  const certSummary = useMemo(() => {
    const p = ctx?.profile;
    const parts: string[] = [];
    if (p?.arcaKeySource === 'generated') parts.push('Clave generada por Vuelto');
    else if (p?.arcaKeySource === 'manual') parts.push('Clave cargada manualmente');
    if (p?.arcaCertExpiresAt) {
      parts.push(`vence el ${new Date(p.arcaCertExpiresAt).toLocaleDateString('es-AR')}`);
    }
    if (p?.arcaCsrCuit) parts.push('falta pegar el certificado');
    return parts.length ? parts.join(' · ') : 'Certificado para facturar.';
  }, [ctx?.profile]);

  const hubContent = (
    <View style={styles.content}>
      <HubTabSegmented value={tab} onChange={setTab} />

      {tab === 'status' ? (
        <>
          <FiscalStatusCard
            profile={ctx?.profile ?? null}
            branchFiscal={ctx?.branchFiscal ?? null}
            ytdTotal={ytdOrg}
            ytdBranchTotal={ytdBranch}
            loading={false}
            showActions={canEditFiscal}
            branchId={currentBranchId}
            layoutMode={layoutWebShell ? 'auto' : 'compact'}
            onActivateProduction={
              ctx?.profile?.environment === 'homologacion' && canEditFiscal ? openProductionSetup : undefined
            }
          />
          {creditNotes.length > 0 ? (
            <View style={styles.card}>
              <Text style={settingsStyles.cardTitle}>Notas de crédito recientes</Text>
              {creditNotes.map((nc) => (
                <View key={nc.id} style={styles.ncRow}>
                  <Text style={styles.ncTitle}>
                    NC {nc.pointOfSale ?? '?'}-{String(nc.invoiceNumber ?? '').padStart(8, '0')}
                  </Text>
                  <Text style={styles.subtitle}>
                    {formatMoney(nc.totalAmount ?? 0)} · CAE {nc.cae ?? 'pendiente'}
                  </Text>
                  {nc.issuedAt ? (
                    <Text style={styles.subtitle}>{formatLocalDateTime(nc.issuedAt)}</Text>
                  ) : null}
                </View>
              ))}
            </View>
          ) : null}
        </>
      ) : null}

      {tab === 'setup' ? (
        <>
          <View style={styles.card}>
            <Text style={settingsStyles.cardTitle}>Datos fiscales</Text>
            <SettingsNavigationRow
              iconName="person-outline"
              title="Régimen y datos del emisor"
              description={emitterSummary}
              onPress={() => navigateInShell(navigation, 'ArcaIntegration', { initialStep: 1 })}
              testID="fiscal.hub.setup-button"
            />
            <SettingsNavigationRow
              iconName="key-outline"
              title="Certificado y punto de venta"
              description={certSummary}
              onPress={() => navigateInShell(navigation, 'ArcaIntegration', { initialStep: 2 })}
              showSeparator={false}
              testID="fiscal.hub.setup-cert"
            />
          </View>
          {ctx?.profile?.taxRegime === 'responsable_inscripto' ? (
            <View style={styles.card}>
              <Text style={settingsStyles.cardTitle}>Herramientas</Text>
              <LibroIvaExportSection branchId={currentBranchId!} />
            </View>
          ) : null}
        </>
      ) : null}

      {tab === 'branches' ? (
        <View style={styles.card}>
          <Text style={settingsStyles.cardTitle}>Puntos de venta por local</Text>
          <BranchFiscalSettingsPanel
            rows={rows}
            currentBranchId={currentBranchId!}
            canEdit={canEditFiscal}
            credentialsValidated={isValidated}
            onPointOfSaleChange={(branchId, value) =>
              setBranchPvMap((prev) => ({ ...prev, [branchId]: value }))
            }
            onValidateBranch={(branchId) => void handleValidateBranch(branchId)}
            onValidateAll={() => {
              setValidatingAll(true);
              void (async () => {
                // BUG-3 fix: try/finally garantiza que setValidatingAll(false) siempre se ejecute,
                // incluso si una validación falla (handleValidateBranch ahora tiene catch propio,
                // pero el try/finally es safety net por si hay un throw no esperado).
                try {
                  for (const row of rows) {
                    const pv = Number(row.pointOfSale);
                    if (!Number.isFinite(pv) || pv <= 0 || row.pvValidatedAt) continue;
                    await handleValidateBranch(row.branchId);
                  }
                } finally {
                  setValidatingAll(false);
                }
              })();
            }}
            validatingAll={validatingAll}
          />
        </View>
      ) : null}
    </View>
  );

  const body = !currentBranchId ? (
    <View style={styles.messageBlock}>
      <Text style={styles.subtitle}>Elegí un local para ver la facturación electrónica.</Text>
    </View>
  ) : !canView ? (
    <View style={styles.messageBlock}>
      <Text style={styles.title}>Sin permiso</Text>
      <Text style={styles.subtitle}>
        No tenés permiso para ver la facturación electrónica. Pedile al dueño que te habilite «Ajustes de sucursal».
      </Text>
      <TouchableOpacity
        style={settingsStyles.primaryButton}
        onPress={() => navigation.goBack()}
        accessibilityRole="button"
      >
        <Text style={settingsStyles.primaryButtonText}>Volver</Text>
      </TouchableOpacity>
    </View>
  ) : loading ? (
    <ShellScreenLoading label="Cargando facturación electrónica…" />
  ) : !isValidated ? (
    <View style={styles.messageBlock}>
      <Text style={styles.title}>Conectá ARCA</Text>
      <Text style={styles.subtitle}>Cargá tus datos, el certificado y el punto de venta.</Text>
      <TouchableOpacity
        style={settingsStyles.primaryButton}
        onPress={() => navigateInShell(navigation, 'ArcaIntegration')}
        accessibilityRole="button"
        testID="fiscal.hub.setup-button"
      >
        <Text style={settingsStyles.primaryButtonText}>Conectar ARCA</Text>
      </TouchableOpacity>
    </View>
  ) : (
    hubContent
  );

  return (
    <ShellContentFrame>
      <ShellScrollScreen
        ref={scrollRef}
        widthVariant="dataWide"
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={() => void onRefresh()}
            colors={[ticketColors.gold]}
            tintColor={ticketColors.gold}
          />
        }
      >
        {body}
      </ShellScrollScreen>
    </ShellContentFrame>
  );
}

const hubTabStyles = StyleSheet.create({
  track: {
    flexDirection: 'row',
    alignSelf: 'stretch',
    gap: 3,
    padding: 3,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.paper2,
  },
  segment: {
    flex: 1,
    minWidth: 0,
    minHeight: 42,
    borderRadius: 9,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.sm,
  },
  segmentActive: {
    backgroundColor: ticketColors.gold,
  },
  segmentText: {
    ...typography.caption,
    fontSize: 13,
    fontWeight: '600',
    color: ticketColors.ink2,
    textAlign: 'center',
  },
  segmentTextActive: {
    color: ticketColors.goldInk,
    fontWeight: '700',
  },
});

const styles = StyleSheet.create({
  content: { padding: spacing.lg, gap: spacing.lg, paddingBottom: spacing.xxl },
  messageBlock: {
    padding: spacing.lg,
    gap: spacing.md,
    paddingBottom: spacing.xxl,
  },
  title: { ...typography.titleXL, color: ticketColors.ink },
  subtitle: { ...typography.body, color: ticketColors.ink2 },
  card: {
    backgroundColor: ticketColors.paper,
    borderRadius: 14,
    padding: spacing.lg,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    gap: spacing.md,
  },
  ncRow: {
    paddingVertical: spacing.sm,
    borderTopWidth: 1,
    borderTopColor: ticketColors.rule,
    gap: spacing.xs,
  },
  ncTitle: { ...typography.body, fontWeight: '600', color: ticketColors.ink },
});
